import { Component, Input, EventEmitter, Output, SimpleChanges, ChangeDetectionStrategy, OnChanges } from '@angular/core';
import { FitCurveCard } from 'src/generated-sources';
import { EChartOption } from 'echarts';
import * as _ from 'lodash';
import { CardAction } from '@features/eda/worksheet/cards/events';
import { ColorsService } from '@shared/graphics/colors.service';
import { smarterNumber } from '@shared/pipes/number-pipes/smarter-number.pipe';

@Component({
    selector: 'fit-curve-card-body',
    templateUrl: './fit-curve-card-body.component.html',
    styleUrls: [
        './fit-curve-card-body.component.less',
        '../../../../shared-styles/fit-table.less',
        '../../../../shared-styles/stats-table.less'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FitCurveCardBodyComponent implements OnChanges {
    @Input() params: FitCurveCard;
    @Input() results: FitCurveCard.FitCurveCardResult;
    @Output() action = new EventEmitter<CardAction>();

    chartOptions: EChartOption;

    constructor(public colorsService: ColorsService) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.results) {
            this.chartOptions = this.results && this.buildChartOptions(this.results);
        }
    }

    buildChartOptions(results: FitCurveCard.FitCurveCardResult): EChartOption {
        const curvesData = results.parametrizedCurves;

        const minMaxReducer = (p: { min: number, max: number }, value: number) => {
            return {
                min: Math.min(p.min, value),
                max: Math.max(p.max, value),
            };
        }
        const minMaxMinMaxReducer = (p: { min: number, max: number }, value: { min: number, max: number }) => {
            return {
                min: Math.min(p.min, value.min),
                max: Math.max(p.max, value.max),
            };
        }
        const initMinMax = {
            min: Number.MAX_VALUE,
            max: -Number.MAX_VALUE,
        };
        let { min: minX, max: maxX } = curvesData[0].plot.x.reduce(minMaxReducer, results.xSeries.reduce(minMaxReducer, initMinMax));

        let { min: minY, max: maxY } = curvesData.map(cd => cd.plot.y.reduce(minMaxReducer, initMinMax))
            .reduce(minMaxMinMaxReducer, results.ySeries.reduce(minMaxReducer, initMinMax));

        minX = Math.floor(minX);
        minY = Math.floor(minY);
        maxX = Math.ceil(maxX);
        maxY = Math.ceil(maxY);

        const curveSeries = curvesData.map((cd, i) => {
            return {
                type: 'line',
                data: _.zip(cd.plot.x, cd.plot.y),
                symbol: 'none',
                itemStyle: {
                    normal: { color: this.colorsService.getColorFromIndex(i) }
                },
            };
        });

        const scatterSeries: EChartOption.SeriesScatter = {
            type: 'scatter',
            z: 0,
            itemStyle: {
                color: '#1e7efa',
                opacity: 0.5
            },
            symbolSize: 4,
            data: _.zip(results.xSeries, results.ySeries),
            large: true
        };

        return {
            color: ['#3398DB'],
            animation: false,
            tooltip: {
                trigger: 'none',
                axisPointer: { type: 'cross' }
            },
            grid: { left: 0, top: 10, right: 0, bottom: 0, containLabel: true },
            xAxis: [{
                type: 'value',
                min: minX,
                max: maxX,
                axisTick: { show: true },
                axisLine: { show: true },
                axisLabel: {
                    color: '#999999',
                    formatter: (value: number) => value === minX || value === maxX ? '' : value
                },
                axisPointer: {
                    label: {
                        formatter: ({ value }: { value: number }) => {
                            return this.params.xColumn.name + ': ' + smarterNumber(value);
                        }
                    }
                }
            }],
            yAxis: {
                type: 'value',
                min: minY,
                max: maxY,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#999999',
                    formatter: (value: number) => value === maxY || value === minY ? '' : value
                },
                axisPointer: {
                    label: {
                        formatter: ({ value }: { value: number }) => {
                            return this.params.yColumn.name + ': ' + smarterNumber(value);
                        }
                    }
                }
            },
            series: [scatterSeries, ...curveSeries]
        };
    }
}
